import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { KIND_LABEL, KIND_CHIP, blankFields, fieldsFromNote, toPayload, noteTitle } from "@/components/shownotes/meta";
import SectionOne from "@/components/shownotes/SectionOne";
import RehearsalSection from "@/components/shownotes/RehearsalSection";
import TimesSection from "@/components/shownotes/TimesSection";
import DeptNotes from "@/components/shownotes/DeptNotes";
import NoteFormActions from "@/components/shownotes/NoteFormActions";
import ReturnDialog from "@/components/shownotes/ReturnDialog";
import TimerCloseConfirm from "@/components/shownotes/TimerCloseConfirm";

// Create / edit / review a Rehearsal or Show Notes report.
export default function NoteForm({ open, onOpenChange, kind: newKind, note, setup, viewOnly, onSaved }) {
  const director = !!setup?.is_director;
  const kind = note?.kind || newKind || "rehearsal";
  const productions = setup?.productions || [];
  const [pid, setPid] = useState("");
  const [fields, setFields] = useState(blankFields);
  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState(null);
  const [returning, setReturning] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const requestClose = (o) => (!o && fields.timer ? setConfirmClose(true) : onOpenChange(o));

  useEffect(() => {
    if (!open) return;
    setPid(note?.production_id || productions[0]?.id || "");
    setFields(note ? fieldsFromNote(note) : blankFields());
  }, [open, note]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !pid) { setCtx(null); return; }
    api.get("/show-notes/context", { params: { production_id: pid, kind } }).then(({ data }) => setCtx(data)).catch(() => setCtx(null));
  }, [open, pid, kind]);

  const set = (patch) => setFields((f) => ({ ...f, ...patch }));
  const save = async (publish, label) => {
    if (!pid) { toast.error("Choose a production group"); return; }
    setBusy(label);
    try {
      const body = { kind, production_id: pid, fields: toPayload(fields), publish };
      const { data } = note ? await api.put(`/show-notes/${note.id}`, body) : await api.post("/show-notes", body);
      toast.success(publish ? `Published ${noteTitle(data)} to ${data.recipient_count} member(s)` : note?.status === "published" ? "Published notes updated" : director ? "Saved" : "Sent to your director for review");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const doReturn = async (reason) => {
    setBusy("return");
    try {
      await api.post(`/show-notes/${note.id}/return`, { reason });
      toast.success("Returned to the stage manager"); setReturning(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto bg-slate-50" data-testid="notes-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${KIND_CHIP[kind]}`}>{KIND_LABEL[kind]}</span>
            {note ? (note.status === "published" ? "Edit published notes" : note.status === "pending" && director ? "Review notes" : "Edit notes") : "New report"}
          </DialogTitle>
          <DialogDescription>
            {note?.status === "published" ? "Changes save quietly — members see the updated notes the next time they open them. The report number stays the same."
              : director ? "Published notes go only to members of the production group you choose." : "Your director reviews these notes before they're shared with the production."}
          </DialogDescription>
        </DialogHeader>
        {note?.status === "returned" && note.return_reason && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="notes-return-reason-banner">Returned: {note.return_reason}</p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Production</span>
          <Select value={pid || undefined} onValueChange={setPid} disabled={!productions.length || note?.status === "published"}>
            <SelectTrigger className="w-64 bg-white" data-testid="notes-production-select"><SelectValue placeholder="No production groups yet" /></SelectTrigger>
            <SelectContent>{productions.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`notes-production-${p.id}`}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-4">
          <SectionOne kind={kind} fields={fields} set={set} note={note} submitterName={setup?.submitter_name} ctx={ctx} />
          {kind === "rehearsal" ? <RehearsalSection fields={fields} set={set} sceneNames={ctx?.scene_names} /> : <TimesSection fields={fields} set={set} />}
          <DeptNotes fields={fields} set={set} />
        </div>
        {!viewOnly && <NoteFormActions director={director} note={note} busy={busy} save={save} onReturn={() => setReturning(true)} />}
        <ReturnDialog open={returning} onOpenChange={setReturning} onConfirm={doReturn} busy={busy === "return"} />
        <TimerCloseConfirm open={confirmClose} onCancel={() => setConfirmClose(false)}
          onConfirm={() => { setConfirmClose(false); set({ timer: null }); onOpenChange(false); }} />
      </DialogContent>
    </Dialog>
  );
}
